import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Formik, yupToFormErrors } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';

import Col from 'components/ui/Col';
import Spacer from 'components/ui/Spacer';
import Row from 'components/ui/Row';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';

import { getTranslatedString } from 'helpers/i18n';
import { featureEnabled } from 'helpers/ToggleBot';

import { modes } from './NoticeHelpers';

import { LegalNotice, NoticeOfEntryNotice } from '../../constants';
import { isLegalNotice, isNoticeOfEntryNotice } from './NoticeHelpers';

import Template from './fields/Template';
import Type from './fields/Type';
import Name from './fields/Name';
import Description from './fields/Description';
import Properties from './fields/Properties';
import Delivery from './fields/Delivery';
import Units from './fields/Units';
import Cotenants from './fields/Cotenants';
import Floors from './fields/Floors';
import Risers from './fields/Risers';
import PostDate from './fields/PostDate';
import ExpirationDate from './fields/ExpirationDate';
import AttachManagementContact from './fields/AttachManagementContact';
import EffectiveDates from './fields/EffectiveDates';
import Image from './fields/Image';
import Attachments from './fields/Attachments';
import HeaderButtons from './HeaderButtons';

const StyledTitle = styled.span`
  font-size: 18px;
  font-family: 'Circular-Bold';
`;
const StyledColMb8 = styled(Col)`
  margin-bottom: 8px;
`;
const StyledSectionContainer = styled.div`
  margin-bottom: 32px;
`;

const NoticeForm = ({ formName, formData, handleSubmit, mode, canOnlySendByNoticeTemplate }) => {
  const calculateMode = (values) => {
    let newMode = null;

    if (values.properties.length === 0) {
      newMode = modes.SELECTING_PROPERTY;
    } else if (values.properties.length === 1 && values.units.length === 0) {
      newMode = modes.SELECTING_PROPERTY_OPTIONS;
    } else if (values.units.length !== 0 && values.cotenants.length !== 0) {
      newMode = modes.SELECTING_UNIT_COTENANTS;
    } else if (values.units.length !== 0) {
      newMode = modes.SELECTING_UNIT;
    } else if (values.floors.length !== 0) {
      newMode = modes.SELECTING_FLOORS;
    } else if (values.risers.length !== 0) {
      newMode = modes.SELECTING_ROWS;
    }

    return newMode;
  };

  const needsTemplateSelection = (values) => {
    return !!canOnlySendByNoticeTemplate && values.notice_template_id === 'none';
  };

  const validateForm = async (values) => {
    const requiredErrorVerbiage = getTranslatedString('required');
    const expiryAfterPostVerbiage = getTranslatedString('expiryPostDate');

    let validationSchema = {
      notice_template_id: Yup.string().required(requiredErrorVerbiage),
      notice_type: Yup.string().required(requiredErrorVerbiage),
      properties: Yup.array().required(requiredErrorVerbiage),
      name: Yup.string().required(requiredErrorVerbiage),
      description: Yup.string().required(requiredErrorVerbiage),
      post_date: Yup.date().required(requiredErrorVerbiage),

      // If delivery is set to units within property, make sure we have some unit(s)
      units: Yup.array().test('units', requiredErrorVerbiage, function (units) {
        if (values.delivery !== 'units_within_property') return true;
        return units.length !== 0;
      }),

      // Cotenants is only required if notice type is legal or notice of entry
      cotenants: Yup.array().test('cotenants', requiredErrorVerbiage, function (cotenants) {
        if (
          featureEnabled('capreit_specific_notification_preferences') ||
          ![LegalNotice, NoticeOfEntryNotice].includes(values.notice_type)
        )
          return true;
        return cotenants.length !== 0;
      }),

      expiration_date: Yup.date().test(
        'expiration_date',
        expiryAfterPostVerbiage,
        function (expiry) {
          if (!values.expiration_date) return true;
          return moment(expiry).isAfter(moment(values.post_date));
        }
      ),
    };
    const schema = Yup.object().shape(validationSchema);
    return schema.validate(values, { abortEarly: false }).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  const initialDelivery = () => {
    if (isLegalNotice(formData) || isNoticeOfEntryNotice(formData)) return 'units_within_property';

    if (formData.units && formData.units.length > 0) return 'units_within_property';
    return 'directly_to_property';
  };

  const initialValues = {
    id: formData.id || undefined,
    notice_template_id: formData.notice_template_id || 'none',
    notice_type: formData.notice_type || undefined,
    cotenants: formData.cotenants || [],
    attach_management_contact: formData.attach_management_contact || false,
    attachments: formData.attachments || [],
    new_attachments: formData.new_attachments || [],
    units: (formData.units && formData.units.map((unit) => unit.value)) || [],
    floors: formData.floors || [],
    risers: formData.risers || [],
    properties:
      (formData.properties && formData.properties.map((property) => property.value)) || [],
    name: formData.name || undefined,
    description: formData.description || undefined,
    post_date: formData.post_date || undefined,
    expiration_date: formData.expiration_date || undefined,
    effective_dates:
      (formData.effective_dates &&
        formData.effective_dates.map((range) => [moment(range[0]), moment(range[1])])) ||
      [],
    image: formData.image || undefined,
    remove_image: formData.remove_image || undefined,
    delivery: formData.delivery || initialDelivery(),
    unit_option: formData.unit_option || 'directly_to_unit',
    mode: formData.mode || undefined,
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validateForm}
      enableReinitialize
    >
      {({ handleSubmit, ...rest }) => {
        // Initialize the mode based on form data
        if (rest.values.mode === undefined) {
          rest.setFieldValue('mode', calculateMode(rest.values));
        }

        return (
          <VerticalFormWrapper id={formName} onSubmit={handleSubmit}>
            <StyledSectionContainer>
              {mode === 'edit' && <HeaderButtons {...rest} />}
              <Template {...rest} />
              {!needsTemplateSelection(rest.values) && (
                <>
                  <Type {...rest} />
                  <Spacer mb={2}>
                    <Row gutter={16}>
                      <StyledColMb8 span={24}>
                        <StyledTitle>
                          {getTranslatedString('landlordPortal.noticeForm.delivery')}
                        </StyledTitle>
                      </StyledColMb8>
                      <Col span={24}>
                        <Spacer mb={1}>
                          <Properties {...rest} />
                        </Spacer>
                        <Spacer mb={1}>
                          <Delivery {...rest} />
                        </Spacer>
                        <Spacer mb={1}>
                          <Units {...rest} />
                        </Spacer>
                        <Spacer mb={1}>
                          <Cotenants {...rest} />
                        </Spacer>
                        <Spacer mb={1}>
                          <Floors {...rest} />
                        </Spacer>
                        <Spacer mb={1}>
                          <Risers {...rest} />
                        </Spacer>
                        <Spacer mb={1}>
                          <PostDate {...rest} />
                        </Spacer>
                        <Spacer mb={1}>
                          <ExpirationDate {...rest} />
                        </Spacer>
                      </Col>
                    </Row>
                  </Spacer>

                  <Spacer mb={2}>
                    <Row gutter={16}>
                      <StyledColMb8 span={24}>
                        <StyledTitle>
                          {getTranslatedString('landlordPortal.noticeForm.noticeDetails')}
                        </StyledTitle>
                      </StyledColMb8>
                      <Col span={24}>
                        <Spacer mb={1}>
                          <Name {...rest} />
                        </Spacer>
                        <Spacer mb={1}>
                          <Description {...rest} />
                        </Spacer>
                        <Spacer mb={1}>
                          <AttachManagementContact {...rest} />
                        </Spacer>
                      </Col>
                    </Row>
                  </Spacer>

                  <Spacer mb={2}>
                    <Row gutter={16}>
                      <StyledColMb8 span={24}>
                        <StyledTitle>
                          {getTranslatedString('landlordPortal.noticeForm.effectiveDates')}
                        </StyledTitle>
                      </StyledColMb8>
                      <Col span={24}>
                        <Spacer mb={1}>
                          <EffectiveDates {...rest} />
                        </Spacer>
                      </Col>
                    </Row>
                  </Spacer>

                  <Spacer mb={2}>
                    <Row gutter={16}>
                      <StyledColMb8 span={24}>
                        <StyledTitle>
                          {getTranslatedString('landlordPortal.noticeForm.attachments')}
                        </StyledTitle>
                      </StyledColMb8>
                      <Col span={24}>
                        <Spacer mb={1}>
                          <Attachments {...rest} />
                        </Spacer>
                      </Col>
                    </Row>
                  </Spacer>

                  <Spacer mb={2}>
                    <Row gutter={16}>
                      <StyledColMb8 span={24}>
                        <StyledTitle>
                          {getTranslatedString('landlordPortal.noticeForm.photo')}
                        </StyledTitle>
                      </StyledColMb8>
                      <Col span={24}>
                        <Spacer mb={1}>
                          <Image {...rest} />
                        </Spacer>
                      </Col>
                    </Row>
                  </Spacer>
                </>
              )}
            </StyledSectionContainer>
          </VerticalFormWrapper>
        );
      }}
    </Formik>
  );
};

export default connect(
  (store) => ({
    canOnlySendByNoticeTemplate: store.userTypes.canOnlySendByNoticeTemplate,
  }),
  {}
)(NoticeForm);
